/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import i18n from '../../../i18n';
import api from '../apiUtils/api';
import headersToObject from '../headersToObject';
import { clearTempStorageKeys } from '../../../utils/auth/clearTempStorageKeys';
import { isTokenExpired } from '../../../utils/auth/isTokenExpired';
import { apiSlice } from '../../apiSlice';
import { getLoginUrl, redirectToLoginIfNeeded } from '../loginRedirectUtils';

/**
 * Create requests to fetch or send resources to the server using the `Fetch` API.
 *
 * IMPORTANT NOTE:
 * For file uploads use the `_xhr.js` which utilizes the `XMLHttpRequest (XHR)` object and allows to abort requests and track upload progress.
 * Use this one for any other type of requests.
 *
 * @param {String} url API endpoint
 * @param {Object} [userOptions = {}] Options to customize the request.
 * @param {Boolean} [userOptions.withAuth] Denotes if should add auth header or not.
 * @param {String} [userOptions.responseType="json"] Rsponse type; tha way to handle the response. Avialbale values: `arrayBuffer`, `blob`, `json`, `text`, `formData`.
 * @param {Object} fetchConfig Required configuration for the `Fetch` API; eg `fetchConfig.method`, `fetchConfig.body`, etc...
 */
function _fetch(url, userOptions = {}, fetchConfig = {}) {
  return function withcontext(context) {
    const defaultUserOptions = {
      withAuth: true,
      responseType: 'json'
    };

    userOptions = {
      ...defaultUserOptions,
      ...userOptions
    };

    fetchConfig.headers = fetchConfig.headers || {};

    if (userOptions.withAuth) {
      fetchConfig.headers.Authorization = `Bearer ${userOptions.token || context._token}`;
    }

    if (i18n.language) {
      fetchConfig.headers['Accept-Language'] = i18n.language;
    }

    return fetch(url, fetchConfig).then(response => {
      const defaultErrorDescription = 'An error occurred with the server.';
      let returnable;

      if (response.ok) {
        // If it's a HEAD request, there's no body to parse, so just resolve with the headers or an empty object
        if (fetchConfig.method === 'HEAD') {
          if (userOptions.includeResponseHeaders) {
            return Promise.resolve({
              headers: headersToObject(response.headers)
            });
          } else {
            return Promise.resolve({});
          }
        }

        // Handle other request types as usual
        returnable = response[userOptions.responseType || 'json']().then(responseContents => {
          let promiseResult;

          if (userOptions.includeResponseHeaders) {
            promiseResult = {
              responseContents: responseContents,
              headers: headersToObject(response.headers)
            };
          } else {
            promiseResult = responseContents;
          }

          return Promise.resolve(promiseResult);
        }).catch(() => {});
      } else {
        // If the refresh token is not expired then no need to log the user out, as the token will be refreshed
        if (response.status === 401 && userOptions.withAuth && (isTokenExpired(context._refreshToken) || !context._refreshToken)) {
          const loginUrl = getLoginUrl();

          clearTempStorageKeys();
          apiSlice.util.resetApiState();
          api.reset();
          if (loginUrl) {
            redirectToLoginIfNeeded(loginUrl);
          }
          return Promise.reject();
        }

        returnable = response.json().then(json => {
          return Promise.reject({
            error: true,
            status: response.status,
            code: json.code || response.status,
            description: json.description || json.message || defaultErrorDescription,
            details: json.details,
            metadata: json.metadata
          });
        }, error => {
          return Promise.reject({
            error,
            status: response.status,
            code: response.status,
            description: response.description || response.message || defaultErrorDescription,
            details: response.details,
            metadata: {}
          });
        });
      }

      return returnable;
    }).catch(err => Promise.reject(err));
  };
}

export default _fetch;
